import { memo, useRef, useState, type KeyboardEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { MoreHorizontal, Pencil, Pin, PinOff, Trash2 } from 'lucide-react'
import { useDeleteConversation, useUpdateConversation } from '@/api/queries'
import type { ConversationSummary } from '@/api/types'
import { cn } from '@/lib/cn'
import { spring } from '@/lib/motion'
import { chatController } from '@/stores/chatController'
import { ConfirmDialog } from '@/components/ui/Dialog'
import { IconButton } from '@/components/ui/IconButton'
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/components/ui/Menu'
import { ActivePill } from './ActivePill'

type Props = {
  conversation: ConversationSummary
  active: boolean
  streaming: boolean
  onNavigate?: () => void
}

export const ConversationItem = memo(function ConversationItem({ conversation, active, streaming, onNavigate }: Props) {
  const { id, title, pinned } = conversation
  const navigate = useNavigate()
  const update = useUpdateConversation()
  const remove = useDeleteConversation()
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(title)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const renameRequested = useRef(false)

  const commitRename = () => {
    const next = draft.trim()
    setRenaming(false)
    if (next && next !== title) update.mutate({ id, patch: { title: next } })
  }

  const onRenameKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitRename()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      setRenaming(false)
    }
  }

  return (
    <motion.li
      layout="position"
      initial={{ opacity: 0, x: -14 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -14, transition: { duration: 0.18 } }}
      transition={spring.smooth}
      className="group relative"
    >
      {renaming ? (
        <input
          autoFocus
          aria-label="Conversation title"
          value={draft}
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onRenameKey}
          onBlur={commitRename}
          onFocus={(e) => e.currentTarget.select()}
          className="h-9 w-full rounded-[10px] bg-surface px-2.5 text-[13.5px] text-ink shadow-[0_0_0_2px_rgb(var(--accent))] outline-none"
        />
      ) : (
        <Link
          to={`/c/${encodeURIComponent(id)}`}
          aria-current={active ? 'page' : undefined}
          onClick={onNavigate}
          className={cn(
            'relative flex h-9 items-center gap-1.5 rounded-[10px] pl-2.5 pr-9 text-[13.5px] transition-colors duration-200',
            active
              ? 'font-medium text-ink'
              : 'text-ink-muted hover:bg-ink/[0.04] hover:text-ink dark:hover:bg-white/[0.05]',
          )}
        >
          {active && <ActivePill />}
          {pinned && <Pin className="relative size-3.5 shrink-0 text-ink-faint" aria-label="Pinned" />}
          <span className="relative truncate">{title || 'Untitled chat'}</span>
          {streaming && <span className="ai-orb relative ml-auto size-2.5 shrink-0" aria-label="Answering" />}
        </Link>
      )}

      {!renaming && (
        <Menu>
          <MenuTrigger asChild>
            <IconButton
              label={`Options for ${title || 'chat'}`}
              tooltip={false}
              size="xs"
              className={cn(
                'absolute right-1 top-1/2 -translate-y-1/2 data-[state=open]:bg-subtle data-[state=open]:opacity-100',
                'focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100',
                active && 'md:opacity-100',
              )}
            >
              <MoreHorizontal />
            </IconButton>
          </MenuTrigger>
          <MenuContent
            align="start"
            onCloseAutoFocus={(event) => {
              // Let the rename input keep focus instead of returning it to the trigger.
              if (renameRequested.current) {
                event.preventDefault()
                renameRequested.current = false
              }
            }}
          >
            <MenuItem
              onSelect={() => {
                renameRequested.current = true
                setDraft(title)
                setRenaming(true)
              }}
            >
              <Pencil /> Rename
            </MenuItem>
            <MenuItem onSelect={() => update.mutate({ id, patch: { pinned: !pinned } })}>
              {pinned ? <PinOff /> : <Pin />} {pinned ? 'Unpin' : 'Pin'}
            </MenuItem>
            <MenuSeparator />
            <MenuItem tone="danger" onSelect={() => setConfirmOpen(true)}>
              <Trash2 /> Delete
            </MenuItem>
          </MenuContent>
        </Menu>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this chat?"
        description={<>&ldquo;{title || 'Untitled chat'}&rdquo; and its messages will be permanently deleted.</>}
        confirmLabel="Delete"
        tone="danger"
        onConfirm={async () => {
          chatController.abortConversation(id)
          try {
            await remove.mutateAsync(id)
            if (active) navigate('/', { replace: true })
          } catch {
            /* the optimistic removal is rolled back by the mutation */
          }
        }}
      />
    </motion.li>
  )
})
